import { useParams, Link } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Coffee, ExternalLink, Calendar, Award, Users, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { CreatorDetailDto } from '@shared/api-contracts';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import { fetchAllPhysicalLocalities, type LocalityOption } from '@/lib/geography';
import { PublicShell } from '@/components/public-shell';
import ClassCard from '@/components/class-card';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AutoRedirect } from '@/components/auto-redirect';

export default function CreatorProfile() {
  const { id } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [tipAmount, setTipAmount] = useState('5');
  const [tipMessage, setTipMessage] = useState('');
  const [showTipDialog, setShowTipDialog] = useState(false);
  const returnToParam = new window.URLSearchParams(window.location.search).get('returnTo');
  const returnHref = returnToParam ? decodeURIComponent(returnToParam) : '/creators';

  // No authentication required for viewing creator profiles

  const {
    data: creator,
    isLoading,
    error,
  } = useQuery<CreatorDetailDto>({
    queryKey: ['/api/creators', id],
  });

  const { data: localities = [] } = useQuery<LocalityOption[]>({
    queryKey: ['all-localities'],
    queryFn: fetchAllPhysicalLocalities,
    staleTime: 1000 * 60 * 10,
  });

  const tipMutation = useMutation({
    mutationFn: async ({ amount, message }: { amount: number; message: string }) => {
      const response = await apiRequest('POST', '/api/payments/create-tip-intent', {
        // The API (and Stripe) take integer cents; the form collects dollars.
        amount: Math.round(amount * 100),
        creatorId: id,
        message,
      });
      const { clientSecret, amount: amountCents } = await response.json();
      // Redirect to checkout page with payment intent (amount in cents, for display)
      window.location.href = `/checkout?client_secret=${clientSecret}&type=tip&creator_id=${id}&amount=${amountCents}`;
    },
    onError: error => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        redirectToLogin(500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to process tip. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Handle unauthorized errors
  // Handle other errors but don't redirect on unauthorized (public page)
  useEffect(() => {
    if (error && !isUnauthorizedError(error as Error)) {
      toast({
        title: 'Error',
        description: 'Failed to load creator profile. Please try again.',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  const handleTip = () => {
    const amount = parseFloat(tipAmount);
    if (isNaN(amount) || amount < 1 || amount > 1000) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a tip between $1 and $1,000.',
        variant: 'destructive',
      });
      return;
    }
    tipMutation.mutate({ amount, message: tipMessage });
  };

  if (authLoading || isLoading) {
    return (
      <PublicShell routeKey="creator-detail">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </PublicShell>
    );
  }

  // Creator profiles are publicly accessible
  if (!creator) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Creator Not Found</h1>
            <p className="text-muted-foreground mb-4">
              The creator you're looking for doesn't exist.
            </p>
            <AutoRedirect />
            <Link href="/creators">
              <Button data-testid="button-back-to-creators">Back to Creators</Button>
            </Link>
          </div>
        </div>
      </PublicShell>
    );
  }

  // Safe display name helpers
  const creatorDisplayName: string = creator.displayName || 'Creator';
  const creatorFirstName: string = creatorDisplayName.split(' ')[0] || creatorDisplayName;
  const creatorLocality = creator.localityId
    ? localities.find(l => l.id === creator.localityId)
    : null;

  const socialLinks = creator.socialLinks || {};

  return (
    <PublicShell routeKey="creator-detail">
      {/* Hero Section */}
      <section className="creator-spotlight py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link href={returnHref}>
            <a className="mb-6 inline-flex text-sm font-medium text-muted-foreground hover:text-foreground">
              ← Back to results
            </a>
          </Link>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Creator Info */}
            <div className="lg:col-span-2">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="relative">
                  <Avatar className="w-32 h-32">
                    <AvatarImage
                      src={
                        creator.profileImageUrl
                          ? creator.profileImageUrl.startsWith('http')
                            ? creator.profileImageUrl
                            : `${window.location.origin}${creator.profileImageUrl}`
                          : undefined
                      }
                      alt={creator.displayName}
                      data-testid="creator-avatar"
                    />
                    <AvatarFallback className="text-2xl">
                      {creatorDisplayName
                        .split(' ')
                        .map((n: string) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  {creator.isVerified && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                      <Award className="w-5 h-5" />
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-foreground" data-testid="creator-name">
                      {creator.displayName}
                    </h1>
                    {creator.isVerified && (
                      <Badge className="bg-primary/10 text-primary">Verified</Badge>
                    )}
                  </div>

                  <p
                    className="text-xl text-accent font-medium mb-4"
                    data-testid="creator-specialty"
                  >
                    {creator.creatorHeadline}
                  </p>

                  {creator.bio && (
                    <p
                      className="mb-4 whitespace-pre-wrap leading-relaxed text-foreground"
                      data-testid="creator-bio"
                    >
                      {creator.bio}
                    </p>
                  )}

                  {creator.experience && (
                    <p className="text-muted-foreground mb-6" data-testid="creator-experience">
                      {creator.experience}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Joined {new Date(creator.createdAt).getFullYear()}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{creator.classes?.length || 0} classes</span>
                    </div>
                    {creatorLocality && (
                      <div className="flex items-center gap-1" data-testid="creator-locality">
                        <MapPin className="w-4 h-4" />
                        <span>
                          {creatorLocality.name}
                          {creatorLocality.regionName ? `, ${creatorLocality.regionName}` : ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Social Links */}
                  {(creator.portfolioUrl || Object.keys(socialLinks).length > 0) && (
                    <div className="flex gap-3 mb-6">
                      {creator.portfolioUrl && (
                        <Button variant="outline" size="sm" asChild data-testid="social-website">
                          <a href={creator.portfolioUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Website
                          </a>
                        </Button>
                      )}
                      {socialLinks.instagram && (
                        <Button variant="outline" size="sm" asChild data-testid="social-instagram">
                          <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                            📷 Instagram
                          </a>
                        </Button>
                      )}
                      {socialLinks.twitter && (
                        <Button variant="outline" size="sm" asChild data-testid="social-twitter">
                          <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                            🐦 Twitter
                          </a>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Support Card */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-accent" />
                    Support {creator.displayName.split(' ')[0]}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Show your appreciation for {creator.displayName.split(' ')[0]}'s amazing work
                    with a tip.
                  </p>

                  {isAuthenticated ? (
                    <Dialog open={showTipDialog} onOpenChange={setShowTipDialog}>
                      <DialogTrigger asChild>
                        <Button
                          className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
                          data-testid="button-send-tip"
                        >
                          <Coffee className="w-4 h-4 mr-2" />
                          Send a Tip
                        </Button>
                      </DialogTrigger>

                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Support {creator.displayName}</DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">
                              Tip Amount ($)
                            </label>
                            <div className="flex gap-2">
                              {['5', '10', '25', '50'].map(amount => (
                                <Button
                                  key={amount}
                                  variant={tipAmount === amount ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setTipAmount(amount)}
                                  data-testid={`tip-preset-${amount}`}
                                >
                                  ${amount}
                                </Button>
                              ))}
                            </div>
                            <Input
                              type="number"
                              min="1"
                              step="0.01"
                              value={tipAmount}
                              onChange={e => setTipAmount(e.target.value)}
                              placeholder="Custom amount"
                              className="mt-2"
                              data-testid="input-tip-amount"
                            />
                          </div>

                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">
                              Message (Optional)
                            </label>
                            <Textarea
                              value={tipMessage}
                              onChange={e => setTipMessage(e.target.value)}
                              placeholder="Say something nice..."
                              rows={3}
                              data-testid="textarea-tip-message"
                            />
                          </div>

                          <Button
                            onClick={handleTip}
                            disabled={tipMutation.isPending}
                            className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                            data-testid="button-confirm-tip"
                          >
                            {tipMutation.isPending ? 'Processing...' : `Send $${tipAmount} Tip`}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <Button
                      onClick={() => redirectToLogin()}
                      className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
                      data-testid="button-login-to-tip"
                    >
                      <Coffee className="w-4 h-4 mr-2" />
                      Login to Send Tip
                    </Button>
                  )}

                  <div className="text-xs text-muted-foreground text-center">
                    Tips go directly to the creator
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Classes Section */}
      {creator.classes && creator.classes.length > 0 && (
        <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-foreground">Classes by {creatorFirstName}</h2>
              <Badge variant="outline">
                {creator.classes.length} {creator.classes.length === 1 ? 'Class' : 'Classes'}
              </Badge>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {creator.classes.map(classItem => (
                <ClassCard
                  key={classItem.id}
                  id={classItem.id}
                  title={classItem.title}
                  description={classItem.description ?? ''}
                  price={classItem.price}
                  pricingMode={classItem.pricingMode}
                  suggestedPrice={classItem.suggestedPrice}
                  promoEnabled={classItem.promoEnabled}
                  promoPrice={classItem.promoPrice}
                  thumbnailUrl={classItem.thumbnailUrl ?? undefined}
                  rating={classItem.rating?.toString()}
                  creator={{
                    id: creator.id,
                    displayName: creator.displayName,
                    profileImageUrl: creator.profileImageUrl ?? undefined,
                  }}
                  spotsLeft={classItem.spotsLeft ?? undefined}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Website Section */}
      {creator.portfolioUrl && (
        <section className="py-16 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Visit {creatorFirstName}'s Website
            </h2>
            <p className="text-muted-foreground mb-8">
              See more amazing work and get inspired by {creatorFirstName}'s creativity.
            </p>
            <Button asChild size="lg" data-testid="button-view-portfolio">
              <a href={creator.portfolioUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                Visit Website
              </a>
            </Button>
          </div>
        </section>
      )}
    </PublicShell>
  );
}
